import React, { useState } from "react";
import { Box, Typography, IconButton, TextField, Tooltip } from "@mui/material";
import { Close as CloseIcon, Check as CheckIcon } from "@mui/icons-material";
import CLINIC_PALETTES, { DEFAULT_PRIMARY } from "../../theme/palettes";
import { generatePalette, getContrastText } from "../../theme/themeUtils";

import { COLORS } from "../../styles/uiConstants";

/**
 * Reusable tema rengi seçici.
 * Hazır paletler + özel hex girişi destekler.
 *
 * @param {string|null} value - Seçili hex renk (null = default)
 * @param {(hex: string|null) => void} onChange - Renk değiştiğinde
 * @param {boolean} [showPreview=true] - Canlı önizleme göster
 */
export default function ThemeColorPicker({ value, onChange, showPreview = true }) {
  const [customHex, setCustomHex] = useState("");
  const activeColor = value || DEFAULT_PRIMARY;
  const preview = generatePalette(activeColor);
  const isValidHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v);

  const handleCustomApply = () => {
    const hex = customHex.startsWith("#") ? customHex : `#${customHex}`;
    if (isValidHex(hex)) {
      onChange(hex);
      setCustomHex("");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: COLORS.black }}>
          Tema Rengi (opsiyonel)
        </Typography>
        {value && (
          <Tooltip title="Varsayılana dön">
            <IconButton
              size="small"
              onClick={() => onChange(null)}
              sx={{ color: COLORS.slate400 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mt: -1 }}>
        Klinik kullanıcılarının panelde göreceği ana renk. Seçilmezse varsayılan mavi kullanılır.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {CLINIC_PALETTES.map((p) => {
          const isSelected = activeColor.toLowerCase() === p.hex.toLowerCase();
          return (
            <Tooltip key={p.id} title={p.label}>
              <Box
                onClick={() => onChange(p.hex)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: p.hex,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: isSelected ? `3px solid ${COLORS.black}` : "2px solid transparent",
                  boxShadow: isSelected ? `0 0 0 2px ${p.hex}40` : undefined,
                  transition: "all 0.15s",
                  "&:hover": {
                    transform: "scale(1.12)",
                    boxShadow: `0 4px 12px ${p.hex}50`,
                  },
                }}
              >
                {isSelected && (
                  <CheckIcon sx={{ color: getContrastText(p.hex), fontSize: 18 }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <TextField
          size="small"
          placeholder="#4f46e5"
          value={customHex}
          onChange={(e) => setCustomHex(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCustomApply()}
          inputProps={{ maxLength: 7 }}
          sx={{
            flex: "0 0 120px",
            "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, fontWeight: 700 },
          }}
        />
        <IconButton
          size="small"
          disabled={!isValidHex(customHex.startsWith("#") ? customHex : `#${customHex}`)}
          onClick={handleCustomApply}
          sx={{
            bgcolor: "primary.main",
            color: "white",
            borderRadius: 1.5,
            "&:hover": { bgcolor: "primary.dark" },
            "&.Mui-disabled": { bgcolor: COLORS.slate200, color: COLORS.slate400 },
          }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" sx={{ color: COLORS.slate400 }}>
          Özel HEX
        </Typography>
      </Box>

      {showPreview && (
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 3,
            border: `1px solid ${COLORS.slate200}`,
            bgcolor: "#fafbfc",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.slate600, mb: 1, display: "block" }}>
            Önizleme
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: preview.primary,
                color: preview.contrastText,
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              Buton
            </Box>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: preview.primaryDark,
                color: getContrastText(preview.primaryDark),
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              Koyu
            </Box>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: preview.primaryLight,
                color: getContrastText(preview.primaryLight),
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              Açık
            </Box>
            <Box
              sx={{
                width: 80,
                height: 8,
                borderRadius: 4,
                background: preview.backgroundGradient,
              }}
            />
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: preview.chipPatientBg,
                color: preview.chipPatientText,
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              Chip
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
